import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { adminAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Settings as SettingsIcon, LogOut, Save, Loader2 } from 'lucide-react';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    stripePublishableKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    paypalClientId: '',
    paypalSecret: '',
    paypalWebhookId: '',
    paypalMode: 'live',
    mailgunApiKey: '',
    mailgunDomain: '',
    mailgunFromEmail: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await adminAPI.settings.get();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings({ ...settings, [name]: value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminAPI.settings.update(settings);
      toast({
        title: 'Success!',
        description: 'Payment settings saved successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    toast({
      title: 'Logged out',
      description: 'You have been logged out successfully'
    });
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Admin Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Payment Settings
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure Stripe and PayPal API Keys
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline">← Back to Dashboard</Button>
            </Link>
            <Link to="/">
              <Button variant="outline">View Site</Button>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSave} className="space-y-8">
            {/* Stripe Settings */}
            <Card className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Stripe Configuration
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enter your Stripe API keys from your Stripe Dashboard
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="stripePublishableKey">
                    Publishable Key
                    <span className="text-xs text-gray-500 ml-2">(Starts with pk_)</span>
                  </Label>
                  <Input
                    id="stripePublishableKey"
                    name="stripePublishableKey"
                    value={settings.stripePublishableKey}
                    onChange={handleInputChange}
                    placeholder="pk_live_..."
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="stripeSecretKey">
                    Secret Key
                    <span className="text-xs text-gray-500 ml-2">(Starts with sk_ or rk_)</span>
                  </Label>
                  <Input
                    id="stripeSecretKey"
                    name="stripeSecretKey"
                    type="password"
                    value={settings.stripeSecretKey}
                    onChange={handleInputChange}
                    placeholder="sk_live_... or rk_live_..."
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="stripeWebhookSecret">
                    Webhook Secret
                    <span className="text-xs text-gray-500 ml-2">(Starts with whsec_)</span>
                  </Label>
                  <Input
                    id="stripeWebhookSecret"
                    name="stripeWebhookSecret"
                    type="password"
                    value={settings.stripeWebhookSecret}
                    onChange={handleInputChange}
                    placeholder="whsec_..."
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </Card>

            {/* PayPal Settings */}
            <Card className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506L9.99 14.254h-.506l-1.187 7.527-.506-.001 2.998-18.999.506.001-1.188 7.527h-.506l1.187-7.527h7.46c1.035 0 1.968.145 2.774.434a5.84 5.84 0 0 1 1.605 1.096z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    PayPal Configuration
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enter your PayPal API credentials from PayPal Developer Dashboard
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="paypalClientId">Client ID</Label>
                  <Input
                    id="paypalClientId"
                    name="paypalClientId"
                    value={settings.paypalClientId}
                    onChange={handleInputChange}
                    placeholder="Enter PayPal Client ID"
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="paypalSecret">Secret</Label>
                  <Input
                    id="paypalSecret"
                    name="paypalSecret"
                    type="password"
                    value={settings.paypalSecret}
                    onChange={handleInputChange}
                    placeholder="Enter PayPal Secret"
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="paypalWebhookId">Webhook ID</Label>
                  <Input
                    id="paypalWebhookId"
                    name="paypalWebhookId"
                    value={settings.paypalWebhookId}
                    onChange={handleInputChange}
                    placeholder="Enter PayPal Webhook ID"
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="paypalMode">Mode</Label>
                  <select
                    id="paypalMode"
                    name="paypalMode"
                    value={settings.paypalMode}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="sandbox">Sandbox (Test)</option>
                    <option value="live">Live (Production)</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Mailgun Email Settings */}
            <Card className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Mailgun Configuration
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure Mailgun for sending email replies (EU region)
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="mailgunApiKey">API Key</Label>
                  <Input
                    id="mailgunApiKey"
                    name="mailgunApiKey"
                    type="password"
                    value={settings.mailgunApiKey}
                    onChange={handleInputChange}
                    placeholder="Enter Mailgun API Key"
                    className="font-mono text-sm"
                    data-testid="mailgun-api-key"
                  />
                </div>

                <div>
                  <Label htmlFor="mailgunDomain">Sending Domain</Label>
                  <Input
                    id="mailgunDomain"
                    name="mailgunDomain"
                    value={settings.mailgunDomain}
                    onChange={handleInputChange}
                    placeholder="e.g. allseoconferences.com"
                    className="font-mono text-sm"
                    data-testid="mailgun-domain"
                  />
                </div>

                <div>
                  <Label htmlFor="mailgunFromEmail">From Email</Label>
                  <Input
                    id="mailgunFromEmail"
                    name="mailgunFromEmail"
                    value={settings.mailgunFromEmail}
                    onChange={handleInputChange}
                    placeholder="e.g. hello@allseoconferences.com"
                    className="font-mono text-sm"
                    data-testid="mailgun-from-email"
                  />
                </div>
              </div>
            </Card>

            {/* Help Text */}
            <Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                ℹ️ Where to find your API keys
              </h3>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li>
                  <strong>Stripe:</strong> Visit{' '}
                  <a
                    href="https://dashboard.stripe.com/apikeys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline"
                  >
                    Stripe Dashboard → Developers → API Keys
                  </a>
                </li>
                <li>
                  <strong>PayPal:</strong> Visit{' '}
                  <a
                    href="https://developer.paypal.com/dashboard/applications/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    PayPal Developer Dashboard → My Apps & Credentials
                  </a>
                </li>
                <li>
                  <strong>Mailgun:</strong> Visit{' '}
                  <a
                    href="https://app.mailgun.com/app/sending/domains"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:underline"
                  >
                    Mailgun Dashboard → Sending → Domain Settings → API Keys
                  </a>
                </li>
              </ul>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;